import React, { useCallback, useState } from 'react';
import { FolderOpen, Plus, Trash2 } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import { FONT, DARK, btnStyle } from './theme';

/**
 * Destination step of the manual ingest flow — pick (or define) a folder
 * template, or fall back to a plain folder picker; resolves the live path
 * preview as template variables are filled in.
 */
export default function DestinationStep({
  c, destPath, chooseDestination,
  templates, selectedTemplateId, setSelectedTemplateId, projectRoot, chooseProjectRoot,
  selectedTemplate, templateVariables, setTemplateVariable, resolvedPath, resolving,
  createTemplate, deleteTemplate,
}) {
  const isDarkMode = c === DARK;
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [createError, setCreateError] = useState(null);

  const inputStyle = {
    fontFamily: FONT, fontSize: '12px', padding: '6px 10px', borderRadius: '6px',
    border: `1px solid ${c.rule}`, background: c.inputBg, color: c.text, outline: 'none',
  };

  const submitNewTemplate = useCallback(async () => {
    if (!newName.trim() || !newPattern.trim()) return;
    setCreateError(null);
    const result = await createTemplate(newName.trim(), newPattern.trim());
    if (result?.success) {
      setCreating(false);
      setNewName('');
      setNewPattern('');
    } else {
      setCreateError(result?.error || 'Failed to save template');
    }
  }, [newName, newPattern, createTemplate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Template picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <PillDropdown
          value={selectedTemplateId || ''}
          onChange={(v) => setSelectedTemplateId(v || null)}
          isDarkMode={isDarkMode}
          options={[{ value: '', label: 'No template — pick a folder directly' }, ...templates.map(t => ({ value: t.id, label: t.name }))]}
        />
        {selectedTemplateId && (
          <button onClick={() => deleteTemplate(selectedTemplateId)} style={btnStyle(c, 'ghost')} title="Delete this template">
            <Trash2 size={12} />
          </button>
        )}
        <button onClick={() => setCreating(v => !v)} style={btnStyle(c, 'ghost')}>
          <Plus size={12} /> New template
        </button>
      </div>

      {/* Inline template creation form */}
      {creating && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${c.rule}`, borderRadius: '8px', background: c.subtleBg }}>
          <input
            value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Template name (e.g. My Project Layout)"
            style={inputStyle}
          />
          <input
            value={newPattern} onChange={(e) => setNewPattern(e.target.value)}
            placeholder="Pattern, e.g. {PROJECT}/{DATE}/{CAMERA}/{CARD_LABEL}"
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
          <div style={{ fontSize: '10px', color: c.muted }}>
            Wrap any word in <code>{'{curly braces}'}</code> to turn it into a blank you fill in later — e.g.{' '}
            <code>{'{PROJECT}/{DATE}'}</code> means every ingest asks you for a project name and a date, then creates
            that folder path under whatever project root you pick.
          </div>
          {createError && <div style={{ fontSize: '11px', color: c.err }}>{createError}</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submitNewTemplate} style={btnStyle(c, 'primary')}>Save template</button>
            <button onClick={() => setCreating(false)} style={btnStyle(c, 'ghost')}>Cancel</button>
          </div>
        </div>
      )}

      {/* No template selected — plain folder picker (original Phase 1+2 behavior) */}
      {!selectedTemplate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={chooseDestination} style={btnStyle(c, 'default')}>
            <FolderOpen size={13} /> {destPath ? 'Change destination' : 'Choose destination'}
          </button>
          {destPath && <span style={{ fontSize: '11px', color: c.muted }}>{destPath}</span>}
        </div>
      )}

      {/* Template selected — project root + dynamic variable inputs + live preview */}
      {selectedTemplate && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '10px', color: c.muted }}>
            <strong>Pattern:</strong> <code>{selectedTemplate.pattern}</code> — pick a project root below (an existing
            folder, e.g. your Desktop or a Projects drive), then fill in each blank. The card&apos;s files get copied
            into the folder path this builds; nothing needs to already exist.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={chooseProjectRoot} style={btnStyle(c, 'default')}>
              <FolderOpen size={13} /> {projectRoot ? 'Change project root' : 'Choose project root'}
            </button>
            {projectRoot && <span style={{ fontSize: '11px', color: c.muted }}>{projectRoot}</span>}
          </div>

          {projectRoot && selectedTemplate.variableNames.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {selectedTemplate.variableNames.map(name => {
                const empty = !String(templateVariables[name] || '').trim();
                return (
                  <div key={name} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '10px', color: empty ? c.warn : c.muted, fontWeight: 600 }}>
                      {name} {empty && '(required)'}
                    </label>
                    <input
                      value={templateVariables[name] || ''}
                      onChange={(e) => setTemplateVariable(name, e.target.value)}
                      placeholder={`Type a value for ${name}`}
                      style={{ ...inputStyle, border: `1px solid ${empty ? c.warn : c.rule}` }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {projectRoot && (
            <div style={{ fontSize: '11px', color: c.muted }}>
              This ingest will create: <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{resolving ? 'Resolving…' : (resolvedPath || '—')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
